import ImageDropUpload from '../components/ImageDropUpload';
import config from '../config.json';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 연구 주제 등록 페이지
function RegisterResearch() {
  const navigate = useNavigate();
  const imageUploadRef = useRef();
  const [formData, setFormData] = useState({
    title: '',
    image_link: '',
    display_order: 0
  });
  const [bullets, setBullets] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Bullet point 변경
  const handleBulletChange = (index, value) => {
    const newBullets = [...bullets];
    newBullets[index] = value;
    setBullets(newBullets);
  };

  // Bullet point 추가
  const addBullet = () => {
    setBullets([...bullets, '']);
  };

  // Bullet point 삭제
  const removeBullet = (index) => {
    const newBullets = bullets.filter((_, i) => i !== index);
    setBullets(newBullets.length > 0 ? newBullets : ['']);
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      setError('Title is required');
      return;
    }

    // 이미지 업로드 (선택된 파일이 있을 때만)
    const uploadedLink = await imageUploadRef.current?.upload();
    if (uploadedLink === false) return;
    const finalFormData = uploadedLink
      ? { ...formData, image_link: uploadedLink }
      : formData;

    try {
      setLoading(true);
      setError(null);

      // 빈 bullet 제거
      const filteredBullets = bullets.filter(b => b.trim() !== '');

      const response = await fetch(`${API_URL}/researches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...finalFormData,
          bullets: filteredBullets
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/researches');
        }, 2000);
      } else {
        setError(data.error || 'Failed to register research');
      }
    } catch (err) {
      console.error('Error registering research:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // 성공 메시지
  if (success) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <h2 style={{ color: 'green' }}>Researches 페이지가 성공적으로 갱신되었습니다.</h2>
        <p>Researches 페이지로 돌아가는 중...</p>
      </div>
    );
  }

  return (
    <div className="site-content">
      <h2 className="section-title">Register a Research Topic</h2>

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            연구 제목 <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Image Upload */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            이미지
          </label>
          <ImageDropUpload
            ref={imageUploadRef}
            uploadDir="research"
            imageBaseUrl={`${config.images.serverUrl}${config.images.basePath}`}
            currentImageLink={formData.image_link}
          />
        </div>

        {/* Bullet Points */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            설명
          </label>
          <small style={{ color: '#666' }}>HTML 태그를 지원합니다.</small>
          {bullets.map((bullet, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                value={bullet}
                onChange={(e) => handleBulletChange(index, e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              {bullets.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBullet(index)}
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addBullet}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            글머리 기호 추가
          </button>
        </div>

        {/* Display Order */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            표시 순서
          </label>
          <small style={{ color: '#666' }}>낮은 숫자가 먼저 표시됩니다. (0: 가장 높은 우선순위)</small>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            min="0"
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <button
            type="button"
            onClick={() => navigate('/researches')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#ddd',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Registering...' : '등록'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RegisterResearch;
